import { createContext, useContext, useState, useEffect } from "react";

// Création du contexte
const AuthContext = createContext(null);

// Hook personnalisé
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth doit être utilisé à l'intérieur d'un AuthProvider");
  }
  return context;
};

// Durée d'expiration en millisecondes (24h)
// const TOKEN_EXPIRATION_TIME = 24 * 60 * 60 * 1000; // 24 heures
const TOKEN_EXPIRATION_TIME = 24 * 60 * 60 * 1000 * 7; // 7 jours

// Fournisseur du contexte
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const authToken = localStorage.getItem("authToken");
    const userId = localStorage.getItem("userId");
    const userRole = localStorage.getItem("userRole");
    const lastLoginTime = localStorage.getItem("lastLoginTime");

    if (authToken && userId && userRole && lastLoginTime) {
      const currentTime = Date.now();
      const loginTime = parseInt(lastLoginTime, 10);
      
      // Vérifie si le token a expiré
      if (currentTime - loginTime > TOKEN_EXPIRATION_TIME) {
        clearUserDatas();
      } else {
        setUser({ authToken, id: userId, role: userRole });
      }
    }
  }, []);

  const login = (userData) => {
    const currentTime = Date.now();
    
    // Mise à jour de l'état utilisateur
    setUser(userData);
    
    // Stockage des données dans localStorage
    localStorage.setItem("authToken", userData.authToken);
    localStorage.setItem("userId", userData.id);
    localStorage.setItem("userRole", userData.role);
    localStorage.setItem("lastLoginTime", currentTime.toString());
  };

  const logout = () => {
    clearUserDatas();
  };

  const clearUserDatas = () => {
    // Réinitialisation de l'état utilisateur
    setUser(null);
    
    // Nettoyage des données d'authentification
    localStorage.removeItem("authToken");
    localStorage.removeItem("userId");
    localStorage.removeItem("userRole");
    localStorage.removeItem("lastLoginTime");
    localStorage.removeItem("lastUserId");
    
    // Nettoyage des données de jeu
    localStorage.removeItem("questionIndex");
    localStorage.removeItem("currentParcoursId");
    localStorage.removeItem("compassLeft");
    localStorage.removeItem("questionStartTime");
    localStorage.removeItem("currentVillageId");
  };

  // Fonction pour vérifier si l'utilisateur est authentifié
  const isAuthenticated = () => {
    // Vérification de l'état utilisateur
    if (!user) return false;
    
    // Vérification de l'expiration du token
    const lastLoginTime = localStorage.getItem("lastLoginTime");
    if (!lastLoginTime) return false;
    
    const currentTime = Date.now();
    const loginTime = parseInt(lastLoginTime, 10);
    
    // Si le token a expiré, déconnecter l'utilisateur
    if (currentTime - loginTime > TOKEN_EXPIRATION_TIME) {
      clearUserDatas();
      return false;
    }
    
    return true;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      logout, 
      clearUserDatas, 
      isAuthenticated 
    }}>
      {children}
    </AuthContext.Provider>
  );
};